// composer/useComposerCaptions.ts — captions for the attached clip (1.27.60). Either the
// uploader's own .srt/.vtt, or — only if they opt in and brought none (owner ruling) — captions
// made on this device by the speech model. Reset whenever the clip changes. The captions are
// sent right before the post is created (the clip is already the uploader's, ready, unposted).
import { useCallback, useEffect, useRef, useState } from 'react';
import { captionLanguageLabel, defaultCaptionLanguage } from '@/features/videos/captions/languages';
import {
  CaptionFileError,
  cuesToVtt,
  readCaptionFile,
  type Cue,
} from '@/features/videos/captions/vtt';
import type { AutoCaptionStage } from '@/features/videos/captions/autoCaptions';

export type ComposerCaptions =
  | { kind: 'none' }
  | { kind: 'file'; name: string; cues: Cue[] }
  | { kind: 'working'; stage: AutoCaptionStage; fraction: number }
  | { kind: 'auto'; cues: Cue[] }
  | { kind: 'error'; message: string };

export interface CaptionUpload {
  language: string;
  label: string;
  source: 'uploaded' | 'auto';
  vtt: string;
}

export function useComposerCaptions(clip: File | null) {
  const [state, setState] = useState<{ clip: File | null; captions: ComposerCaptions }>({
    clip: null,
    captions: { kind: 'none' },
  });
  const [language, setLanguage] = useState(defaultCaptionLanguage);
  const abortRef = useRef<AbortController | null>(null);
  const captions: ComposerCaptions = state.clip === clip ? state.captions : { kind: 'none' };

  const put = useCallback((next: ComposerCaptions) => setState({ clip, captions: next }), [clip]);

  // A new clip (or none) stops any caption-making for the old one.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
    },
    [clip],
  );

  async function pickFile(file: File) {
    try {
      const { cues } = await readCaptionFile(file);
      put({ kind: 'file', name: file.name, cues });
    } catch (e) {
      put({
        kind: 'error',
        message: e instanceof CaptionFileError ? e.message : 'That caption file could not be read.',
      });
    }
  }

  async function makeAutomatically() {
    if (!clip) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    put({ kind: 'working', stage: 'reading', fraction: 0 });
    try {
      const { makeCaptions } = await import('@/features/videos/captions/autoCaptions');
      const cues = await makeCaptions(clip, {
        language,
        signal: controller.signal,
        onProgress: (stage, fraction) => {
          if (!controller.signal.aborted) put({ kind: 'working', stage, fraction });
        },
      });
      if (controller.signal.aborted) return;
      put(
        cues.length
          ? { kind: 'auto', cues }
          : { kind: 'error', message: 'No speech was found in this video.' },
      );
    } catch (e) {
      if (controller.signal.aborted || (e instanceof DOMException && e.name === 'AbortError')) {
        return;
      }
      put({
        kind: 'error',
        message:
          e instanceof Error && e.message
            ? `Captions couldn’t be made on this device: ${e.message}`
            : 'Captions couldn’t be made on this device.',
      });
    }
  }

  function clear() {
    abortRef.current?.abort();
    abortRef.current = null;
    put({ kind: 'none' });
  }

  /** What to send with the post, or null for no captions. */
  function upload(): CaptionUpload | null {
    if (captions.kind !== 'file' && captions.kind !== 'auto') return null;
    const label = captionLanguageLabel(language);
    return {
      language,
      label,
      source: captions.kind === 'file' ? 'uploaded' : 'auto',
      vtt: cuesToVtt(captions.cues),
    };
  }

  return {
    captions,
    language,
    setLanguage,
    pickFile,
    makeAutomatically,
    /** The uploader's corrections to automatic captions. */
    setCues: (cues: Cue[]) => put({ kind: 'auto', cues }),
    clear,
    upload,
    busy: captions.kind === 'working',
  };
}

export type ComposerCaptionsHook = ReturnType<typeof useComposerCaptions>;
